import { useEffect, useRef, type ReactNode } from 'react';
import { Button, Group, Loader, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import type { admin } from '@repletoryreach/sdk-api';
import { useTestAiModel } from '../../../../data/useAiModels';
import { formatInteger } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { testCosts } from '../aiModels';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Test runs as soon as the dialog opens, and the platform pays: a text model replies with one word, a speech model
 * reads a short sentence aloud, and speech to text stores, transcribes and deletes a two-second recording. A picture
 * model makes one picture, which costs real money, so that test waits for a click. The answer, the time and the
 * figures show, or the error in words. A stand-in answer (AWS is not set up here) is labelled as one, so nobody
 * switches a model on believing it answered.
 */
export function AiModelTestModal({ model, onClose, onResult, onSwitchOn, testCost }: {
  model: admin.AdminAiModel | null;
  onClose: () => void;
  onResult: (code: string, result: admin.AiModelTest) => void;
  onSwitchOn: (m: admin.AdminAiModel) => void;
  /** A picture test's cost to the platform, in words; null when the price book has none. */
  testCost?: string | null;
}) {
  const t = useT();
  const test = useTestAiModel();
  // The parent's callback changes on every render; the run must not.
  const report = useRef(onResult);
  report.current = onResult;

  function run(m: admin.AdminAiModel) {
    test.mutate(m.code, { onSuccess: (res) => report.current(m.code, res.data) });
  }

  useEffect(() => {
    if (!model) { test.reset(); return; }
    if (!testCosts(model)) run(model);
    // Once per model opened: Test again is a deliberate click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.code]);

  const result = test.data?.data;
  const kind = model?.kind ?? 'text';
  const waiting = model != null && testCosts(model) && !test.data && !test.isPending && !test.isError;

  return (
    <Modal
      opened={model != null}
      onClose={onClose}
      title={model ? t('billing.aiModels.test.title', { name: model.name }) : ''}
      size="md"
    >
      {model && (
        <Stack gap="md">
          {waiting && (
            <Alert color="warning" title={t('billing.aiModels.test.costsTitle')}>
              {testCost ? t('billing.aiModels.test.costsBody', { cost: testCost }) : t('billing.aiModels.test.costsBodyUnpriced')}
            </Alert>
          )}

          {test.isPending && (
            <Group gap="sm" wrap="nowrap">
              <Loader size="sm" />
              <Text role="bodySmall">{t(kind === 'text' ? 'billing.aiModels.test.running' : `billing.aiModels.test.running.${kind}`, { name: model.name })}</Text>
            </Group>
          )}

          {test.isError && (
            <Alert color="error" title={t('billing.aiModels.test.couldNotRun')}>
              {test.error instanceof Error ? test.error.message : t('common.unknownError')}
            </Alert>
          )}

          {result && !test.isPending && (
            <Stack gap="md">
              {result.ok && !result.stub && (
                <Alert color="success" title={t('billing.aiModels.test.answered', { name: model.name })}>
                  {t(kind === 'text' ? 'billing.aiModels.test.answeredBody' : 'billing.aiModels.test.answeredBodyOther')}
                </Alert>
              )}
              {result.ok && result.stub && (
                <Alert color="warning" title={t('billing.aiModels.test.stubTitle')}>
                  {t('billing.aiModels.test.stubBody', { name: model.name })}
                </Alert>
              )}
              {!result.ok && (
                <Alert color="error" title={t('billing.aiModels.test.failed', { name: model.name })}>
                  <Stack gap="xs">
                    <Text role="bodySmall">{result.error || t('common.unknownError')}</Text>
                    <Text role="bodySmall">{t(kind === 'text' ? 'billing.aiModels.test.failedHint' : `billing.aiModels.test.failedHint.${kind}`)}</Text>
                  </Stack>
                </Alert>
              )}

              {result.ok && (
                <Stack gap="xs">
                  <Text role="overline" c={MUTED}>{t('billing.aiModels.test.answer')}</Text>
                  <blockquote className="rr-aimodels-answer">
                    <Text role="bodySmall">{result.answer?.trim() || t('billing.aiModels.test.emptyAnswer')}</Text>
                  </blockquote>
                </Stack>
              )}

              <dl className="rr-aimodels-figures">
                <Figure label={t('billing.aiModels.test.time')} value={t('billing.aiModels.test.ms', { ms: formatInteger(result.milliseconds) })} />
                {result.ok && kind === 'text' && (
                  <>
                    <Figure label={t('billing.aiModels.test.read')} value={t('billing.aiModels.test.tokens', { count: formatInteger(result.input_tokens) })} />
                    <Figure label={t('billing.aiModels.test.written')} value={t('billing.aiModels.test.tokens', { count: formatInteger(result.output_tokens) })} />
                  </>
                )}
                {result.ok && kind === 'speech' && (
                  <Figure label={t('billing.aiModels.test.characters')} value={formatInteger(result.input_tokens)} />
                )}
                <Figure
                  // Speech to text answers with the bucket it used: that is what its test proves.
                  label={t(kind === 'transcribe' ? 'billing.aiModels.test.bucket' : 'billing.aiModels.test.modelId')}
                  value={result.model_id?.trim() ? <code>{result.model_id}</code> : t('billing.aiModels.modelId.platform')}
                />
              </dl>
            </Stack>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={onClose}>{t('action.close')}</Button>
            {waiting
              ? <Button loading={test.isPending} onClick={() => run(model)}>{t('billing.aiModels.test.runPaid')}</Button>
              : <Button variant="outline" loading={test.isPending} onClick={() => run(model)}>{t('billing.aiModels.test.again')}</Button>}
            {result?.ok && !result.stub && !model.enabled && !test.isPending && (
              <Button onClick={() => onSwitchOn(model)}>{t('billing.aiModels.test.switchOn')}</Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function Figure({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt><Text role="overline" c={MUTED}>{label}</Text></dt>
      <dd><Text role="bodySmall" fw={600} className="rr-aimodels-wrap">{value}</Text></dd>
    </div>
  );
}
